import React, { useState, useEffect, useRef, useCallback } from 'react'
import Uppy from '@uppy/core'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import AwesomeFileInput from 'components/ui/forms/field/awesomeFileInput/AwesomeFileInput'
import { UploadFileStatusEnum } from 'enum/uploadFile-status.enum'
import { ErrorToast } from 'components/ui/toast/toastsTemplates'
import { answerTask } from 'models/answerTask.model'
import { getS3FileId } from 'helpers/converters.helper'
import * as fromActions from 'store/actions/answer-task.action'
import { IMainState } from 'store/states'
import MandatoryQuestion from 'components/ui/mandatory-question/MandatoryQuestion'
import { gbToBytes } from 'helpers/others.helper'
import { BlockTaskContent } from 'store/states/lessons.state'
import { useCourse, useLesson } from 'contexts'

interface ITask {
  id: string
  canEdit?: boolean
  data: BlockTaskContent
  refArray: any
  index: number
}

// Restricciones
const ONE_GB = gbToBytes(1)

const Task: React.FC<ITask> = ({
  data,
  id: questionableId,
  canEdit = true,
  refArray,
  index,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [filesStatus, setFileStatus] = useState([] as any)
  const [errorFile, setErrorFile] = useState({} as any)
  const [newChange, setNewChange] = useState(false)
  const [filesArray, setFilesArray] = useState({} as any)
  const [answers, setAnswers] = useState([] as any)
  // Cómo la carga de archivos se hace usando sockets, la únicamanera que encontré para evitar el "closure"
  // fue usando references de React
  const ref = useRef({} as any)
  const cancel = useRef('')
  const edit = useRef(true)
  // Los socket marean a los setState

  const { submit } = useLesson()
  const { setMandatoryTaskUploadInProgress } = useCourse()

  const syncMandatoryTaskUploadInProgress = useCallback(() => {
    if (!data.mandatory) return

    let inProgress = false
    for (const id in ref.current) {
      if (ref.current[id]?.status === UploadFileStatusEnum.inProcess) {
        inProgress = true
        break
      }
    }
    setMandatoryTaskUploadInProgress(inProgress)
  }, [data.mandatory, setMandatoryTaskUploadInProgress])

  useEffect(() => {
    edit.current = canEdit
  }, [canEdit])

  // Creado para cambiar el id del bloque por la entregada por el back.
  // Así es posible eliminar la referencia creada en bd un archivo recien cargado
  const { ids } = useSelector(({ answer }: IMainState) => answer)
  useEffect(() => {
    if (ids.length > 0) {
      const auxId = ids.map(({ id, uppyId }: any) => {
        if (ref.current[uppyId]) {
          const aux = { ...ref.current[uppyId], id }
          delete ref.current[uppyId]
          ref.current[id] = aux
        }
        return id
      })
      setNewChange(true)
      setAnswers((current) => [...current, ...auxId])
    }
  }, [ids])

  const preventPageReload = (event: any) => {
    for (const id in ref.current) {
      if (ref.current[id].status === UploadFileStatusEnum.inProcess) {
        event.preventDefault()
        event.returnValue = true
      }
    }
  }

  useEffect(() => {
    window.addEventListener('beforeunload', preventPageReload)
    return () => {
      window.removeEventListener('beforeunload', preventPageReload)
    }
  }, [])

  // Carga inicial de datos
  // Se guarda un arreglo con los ids de bd para mantener consistencia en la funcion de elimianr
  useEffect(() => {
    ref.current = {}
    const ids = [] as any

    const attachments = data.response?.attachments
    attachments?.forEach(
      ({
        id,
        uploadUrl,
        fileData: {
          metadata: { size, filename: name },
        },
        token,
      }) => {
        id && ids.push(token)
        ref.current[token] = {
          id,
          questionableId,
          name,
          size,
          progress: 0,
          status: UploadFileStatusEnum.completed,
          download: uploadUrl,
          token,
        }
      }
    )
    setAnswers(ids)
    setNewChange(true)
    syncMandatoryTaskUploadInProgress()
  }, [data, questionableId, syncMandatoryTaskUploadInProgress])

  // Inicio de la instancia de uppy 💀
  const uppy = Uppy({
    restrictions: {
      maxFileSize: ONE_GB,
    },
  }).use(AwsS3Multipart, {
    companionUrl: `//${process.env.REACT_APP_API_URL}`,
  })

  const isTheSameFile = (filename: string) => {
    for (const id in ref.current) {
      if (ref.current[id].name === filename) return true
    }
    return false
  }

  const onSelectFiles = (files: any) => {
    if (files && files.length > 0 && edit.current) {
      files.map((file: any) => {
        if (!isTheSameFile(file.name)) {
          if (file.size < ONE_GB) {
            try {
              uppy.addFile({
                id: questionableId,
                source: 'taskBlock',
                name: file.name,
                type: file.type,
                data: file,
              })
              setFilesArray((current: any) => ({
                ...current,
                [file.name]: file,
              }))
            } catch (err) {
              ErrorToast(
                t(`lessons.contentBlocks.taskBlock.toast.uploadProblems`)
              )
            }
          } else {
            ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.sizeExceeded`))
          }
        } else {
          ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.sameName`))
        }
      })
      uppy.upload()
    }
  }

  uppy.on('upload', () => {
    if (!data.multiFile) {
      const toDelete = Object.keys(ref.current)[0]
      cancel.current = toDelete
      ref.current = {}
    }
  })

  uppy.on(
    'upload-progress',
    ({ id, name, size }, { bytesUploaded, bytesTotal }) => {
      // @ts-ignore
      const percentage = (bytesUploaded / bytesTotal).toFixed(2) * 100
      if (id !== cancel.current) {
        ref.current[id] = {
          id,
          questionableId,
          name,
          size,
          progress: percentage,
          status: UploadFileStatusEnum.inProcess,
        }
        setNewChange(true)
      }
      // Si se desea que un archivo que se está subiendo sea cancelado
      // Solo desde un socket tengo acceso a las funciones de Uppy
      if (cancel.current != '') {
        uppy.getFile(cancel.current) && uppy.removeFile(cancel.current)
      }
    }
  )

  uppy.on('upload-success', ({ id, name, size, type }, { uploadURL }) => {
    ref.current[id] = {
      id,
      questionableId,
      name,
      size,
      progress: 100,
      status: UploadFileStatusEnum.completed,
      download: uploadURL,
    }
    setNewChange(true)

    // Preparar la data para enviarla al back
    const fileArray = [] as any
    fileArray.push({
      file: {
        id: getS3FileId(uploadURL),
        storage: 'cache',
        metadata: {
          uppyId: id,
          filename: name,
          mimeType: type,
          size,
        },
      },
    })
    const { blockInstanceableId } = data
    const newTask = answerTask(blockInstanceableId, fileArray)
    dispatch({
      type: fromActions.ANSWER_TASK_CREATE,
      payload: { task: newTask, index },
    })
  })

  uppy.on('file-removed', ({ id }) => {
    if (cancel.current === id) {
      cancel.current = ''
    }
  })

  uppy.on('upload-error', ({ id, name, size }, error) => {
    const result = {} as any
    result[id] = {
      id,
      questionableId,
      name,
      size,
      progress: 0,
      status: UploadFileStatusEnum.error,
      error,
    }
    setErrorFile(result)
  })

  useEffect(() => {
    if (Object.values(errorFile).length > 0) {
      for (const id in errorFile) {
        if (ref.current[id] && !ref.current[id].error) {
          ref.current[id] = errorFile[id]
          setNewChange(true)
        }
      }
    }
  }, [errorFile])

  useEffect(() => {
    if (newChange) {
      setFileStatus(Object.values(ref.current))
      setNewChange(false)
      syncMandatoryTaskUploadInProgress()
    }
  }, [newChange, syncMandatoryTaskUploadInProgress])

  useEffect(() => {
    return () => {
      setMandatoryTaskUploadInProgress(false)
    }
  }, [setMandatoryTaskUploadInProgress])

  const handleRetry = (name: any, id: string) => {
    try {
      const file = filesArray[name]
      if (file) {
        uppy.addFile({
          source: 'taskBlock',
          name: file.name,
          type: file.type,
          data: file,
        })
        uppy.upload()
        setFilesArray((current: any) => ({ ...current, [file.name]: file }))
        delete ref.current[id]
        setNewChange(true)
      }
    } catch (error) {
      ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.uploadProblems`))
    }
  }

  const handleCancel = (token: string) => {
    // Solo si existe en el arreglo de archivos en back
    answers.includes(token) &&
      dispatch({
        type: fromActions.ANSWER_TASK_DELETE,
        payload: { token, index },
      })
    cancel.current = token
    delete ref.current[token]
    setNewChange(true)
  }

  return (
    <div id="task" ref={refArray}>
      <AwesomeFileInput
        id={questionableId}
        disabled={!edit.current}
        files={filesStatus}
        sizeLimit={ONE_GB}
        multiFile={data.multiFile}
        onCancel={handleCancel}
        onRetry={handleRetry}
        onChange={onSelectFiles}
      />
      <MandatoryQuestion show={submit && data.mandatory} />
    </div>
  )
}

export default Task
